package com.anjuros.website.templates.template10.sections

import androidx.compose.runtime.Composable
import com.anjuros.website.components.language.LocalText
import com.anjuros.website.components.shared.AnjurosImage
import com.anjuros.website.components.shared.AnjurosText
import com.anjuros.website.templates.template10.components.Template10Eyebrow
import com.anjuros.website.templates.template10.components.Template10Reveal
import com.anjuros.website.templates.template10.components.Template10Seal
import com.anjuros.website.templates.template10.components.template10BodyFont
import com.anjuros.website.templates.template10.components.template10HeadingFont
import com.anjuros.website.templates.template10.modules.Template10Section
import com.anjuros.website.templates.template10.modules.Template10Theme
import com.anjuros.website.templates.template10.styles.Template10AboutImageStyle
import com.anjuros.website.templates.template10.styles.Template10ContainerStyle
import com.anjuros.website.templates.template10.styles.Template10QuoteStyle
import com.anjuros.website.templates.template10.styles.Template10SectionPaddingStyle
import com.anjuros.website.templates.template10.util.ResKey
import com.anjuros.website.templates.template10.util.Template10Constants
import com.anjuros.website.templates.template10.util.Template10Glyphs
import com.anjuros.website.templates.template10.util.Template10Res
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.ObjectFit
import com.varabyte.kobweb.compose.css.TextTransform
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.border
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxSize
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.flexWrap
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.id
import com.varabyte.kobweb.compose.ui.modifiers.letterSpacing
import com.varabyte.kobweb.compose.ui.modifiers.lineHeight
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.objectFit
import com.varabyte.kobweb.compose.ui.modifiers.overflow
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.textTransform
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.silk.components.layout.SimpleGrid
import com.varabyte.kobweb.silk.components.layout.numColumns
import com.varabyte.kobweb.silk.style.toModifier
import org.jetbrains.compose.web.css.FlexWrap
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.em
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

@Composable
fun Template10AboutSection() {
    Box(
        modifier = Template10SectionPaddingStyle.toModifier()
            .id(Template10Section.AboutMe.id)
            .fillMaxWidth()
            .backgroundColor(Template10Theme.PaperDark.rgb)
            .styleModifier {
                property("border-top", "1px solid ${Template10Theme.Line.hex}")
                property("border-bottom", "1px solid ${Template10Theme.Line.hex}")
            },
        contentAlignment = Alignment.Center
    ) {
        SimpleGrid(
            numColumns = numColumns(base = 1, lg = 2),
            modifier = Template10ContainerStyle.toModifier()
                .gap(72.px)
                .styleModifier { property("align-items", "center") }
        ) {
            Template10Reveal(modifier = Modifier.fillMaxWidth()) {
                Template10AboutPortrait()
            }
            Template10Reveal(modifier = Modifier.fillMaxWidth(), delayMs = 150) {
                Template10AboutText()
            }
        }
    }
}

@Composable
private fun Template10AboutPortrait() {
    Box(
        modifier = Template10AboutImageStyle.toModifier()
            .fillMaxWidth()
            .margin(bottom = 28.px)
    ) {
        AnjurosImage(
            modifier = Modifier
                .fillMaxSize()
                .borderRadius(12.px)
                .objectFit(ObjectFit.Cover),
            src = Template10Res.Image.about,
            alt = "Tai Chi instructor portrait"
        )

        // vertical "master" seal
        Template10Seal(
            modifier = Modifier
                .align(Alignment.TopEnd)
                .margin(top = 20.px, right = 20.px),
            glyph = Template10Glyphs.MASTER,
            fontSize = 20,
            vertical = true
        )

        // years badge, hanging off the bottom-left corner
        Column(
            modifier = Modifier
                .align(Alignment.BottomStart)
                .styleModifier {
                    property("margin-left", "-16px")
                    property("margin-bottom", "-28px")
                }
                .backgroundColor(Template10Theme.Ink.rgb)
                .borderRadius(8.px)
                .padding(topBottom = 22.px, leftRight = 26.px)
                .gap(2.px)
        ) {
            Span(
                attrs = Modifier
                    .template10HeadingFont()
                    .fontSize(40.px)
                    .fontWeight(FontWeight.ExtraBold)
                    .lineHeight(1)
                    .color(Template10Theme.Paper.rgb)
                    .toAttrs()
            ) {
                AnjurosText(
                    Template10Constants.EXPERIENCE,
                    com.anjuros.website.components.language.ResKey.PLACEHOLDER_EMPTY
                )
            }
            Span(
                attrs = Modifier
                    .template10BodyFont()
                    .fontSize(13.px)
                    .fontWeight(FontWeight.SemiBold)
                    .letterSpacing(0.08.em)
                    .textTransform(TextTransform.Uppercase)
                    .color(Template10Theme.Paper.rgb)
                    .toAttrs()
            ) {
                Text(LocalText(ResKey.TEMPLATE10_EXPERIENCE_YEARS))
            }
        }
    }
}

@Composable
private fun Template10AboutText() {
    Column(modifier = Modifier.fillMaxWidth().gap(22.px)) {
        Template10Eyebrow(text = ResKey.TEMPLATE10_SECTION2)
        P(
            attrs = Template10QuoteStyle.toModifier()
                .margin(0.px)
                // hanging opening quote: lines 2+ line up with the first letter
                .styleModifier {
                    property("padding-left", "0.42em")
                    property("text-indent", "-0.42em")
                }
                .template10HeadingFont()
                .fontWeight(FontWeight.Bold)
                .color(Template10Theme.Ink.rgb)
                .toAttrs()
        ) {
            Text(LocalText(ResKey.TEMPLATE10_ABOUT_TITLE))
        }
        listOf(ResKey.TEMPLATE10_ABOUT_PARAGRAPH1, ResKey.TEMPLATE10_ABOUT_PARAGRAPH2).forEach { paragraph ->
            P(
                attrs = Modifier
                    .margin(0.px)
                    .template10BodyFont()
                    .fontSize(17.px)
                    .lineHeight(1.65)
                    .color(Template10Theme.Muted.rgb)
                    .toAttrs()
            ) {
                Text(LocalText(paragraph))
            }
        }
        Row(
            modifier = Modifier
                .flexWrap(FlexWrap.Wrap)
                .gap(10.px)
                .margin(top = 6.px)
        ) {
            listOf(ResKey.TEMPLATE10_BADGE1, ResKey.TEMPLATE10_BADGE2, ResKey.TEMPLATE10_BADGE3).forEach { badge ->
                Span(
                    attrs = Modifier
                        .border(width = 1.px, style = LineStyle.Solid, color = Template10Theme.Ink.rgb)
                        .borderRadius(999.px)
                        .padding(topBottom = 8.px, leftRight = 16.px)
                        .template10BodyFont()
                        .fontSize(14.px)
                        .fontWeight(FontWeight.SemiBold)
                        .color(Template10Theme.Ink.rgb)
                        .toAttrs()
                ) {
                    Text(LocalText(badge))
                }
            }
        }
    }
}
